'use client';

import ListPageLoadingShell from '@/components/ListPageLoadingShell';
import ProductLoadingSkeleton from '@/components/ProductLoadingSkeleton';
import {
  getInstantRouteTransitionKind,
  SOFT_NAV_SKELETON_TIMEOUT_MS,
  type InstantRouteTransitionKind,
} from '@/lib/instantRouteTransition';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useSyncExternalStore } from 'react';

type PendingNav = {
  href: string;
  pathname: string;
  fromPathname: string;
  startedAt: number;
  kind: InstantRouteTransitionKind;
} | null;

function normalizePathname(path: string) {
  if (!path) return '/';
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return withSlash.length > 1 && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
}

let pending: PendingNav = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return pending;
}

function getServerSnapshot(): PendingNav {
  return null;
}

function resolveHref(href: string): URL | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(href, window.location.origin);
  } catch {
    return null;
  }
}

/** Call right before router.push / Link navigation that must feel instant. */
export function beginInstantRouteTransition(href: string) {
  if (typeof window === 'undefined') return;

  const url = resolveHref(href);
  if (!url) return;
  if (url.origin !== window.location.origin) return;

  const kind = getInstantRouteTransitionKind(url.pathname);
  if (!kind) return;

  const fromPathname = normalizePathname(window.location.pathname);
  const pathname = normalizePathname(url.pathname);

  // Same-path query changes (sort/filter) use UrunlerListingGate — skip overlay.
  if (pathname === fromPathname) return;

  pending = {
    href: `${pathname}${url.search}`,
    pathname,
    fromPathname,
    startedAt: Date.now(),
    kind,
  };
  emit();
}

export function clearInstantRouteTransition() {
  if (!pending) return;
  pending = null;
  emit();
}

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

/**
 * Listing/product skeleton below the header while soft-nav RSC is still pending.
 * Replaces segment loading.tsx so hard requests can still return HTTP 404 from notFound().
 */
export default function InstantRouteTransitionOverlay() {
  const pathname = usePathname();
  const nav = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [visible, setVisible] = useState(false);
  const [topOffset, setTopOffset] = useState(0);

  // Capture-phase only observes; never preventDefault — GTM / attribution click handlers stay intact.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || isModifiedClick(event)) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      beginInstantRouteTransition(href);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    const onAbort = () => clearInstantRouteTransition();
    window.addEventListener('popstate', onAbort);
    window.addEventListener('pageshow', onAbort);
    return () => {
      window.removeEventListener('popstate', onAbort);
      window.removeEventListener('pageshow', onAbort);
    };
  }, []);

  useEffect(() => {
    if (!nav) {
      setVisible(false);
      return;
    }
    const headerBottom = document.getElementById('hc-search')?.getBoundingClientRect().bottom ?? 0;
    setTopOffset(Math.max(0, headerBottom));
    setVisible(true);

    const t = window.setTimeout(() => {
      clearInstantRouteTransition();
    }, SOFT_NAV_SKELETON_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [nav]);

  useEffect(() => {
    if (!nav) return;

    const current = normalizePathname(pathname);

    // Bölge: URL erken değişebilir ama RSC geç gelir — overlay'i sayfa beacon temizler.
    // Geri tuşu popstate/pageshow ile temizlenir; burada fromPathname'e bakmak
    // tıklamanın ilk karesinde overlay'i hemen kapatır.
    if (nav.pathname.includes('/bolge/')) return;

    const arrivedAtTarget = current === nav.pathname;
    const redirectedAway = current !== nav.fromPathname && current !== nav.pathname;
    if (arrivedAtTarget || redirectedAway) {
      clearInstantRouteTransition();
    }
  }, [pathname, nav]);

  if (!visible || !nav) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 overflow-y-auto bg-hzc_beige_200"
      style={{ top: topOffset }}
      aria-busy="true"
      aria-live="polite"
    >
      {nav.kind === 'product' ? (
        <div className="px-2 md:px-4 mt-4 mb-16 bg-hzc_beige_200">
          <ProductLoadingSkeleton />
        </div>
      ) : (
        <ListPageLoadingShell />
      )}
    </div>
  );
}
